import { useState } from "react";
import { X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { log } from "../lib/log";
import { STEPS } from "./wellness/constants";
import { StepPicker } from "./wellness/StepPicker";
import { PlanResult } from "./wellness/PlanResult";

export function WellnessPlannerModal({ open, onClose, destination = null }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (final) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/wellness-plan", destination ? { ...final, destination } : final);
      setPlan(data);
    } catch (e) {
      log.error("Wellness plan generation failed:", e);
      setError("Could not generate your journey. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const pick = (field, val) => {
    const next = { ...answers, [field]: val };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else submit(next);
  };

  const reset = () => { setStep(0); setAnswers({}); setPlan(null); setError(null); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="wellness-planner-modal" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border-2 border-[#C8A96E]/70 p-8"
        style={{ background: "radial-gradient(circle at 20% 10%, #4A335633 0%, transparent 60%), linear-gradient(180deg, #1C1622 0%, #15111A 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button data-testid="wellness-close-btn" className="absolute top-4 right-4 text-[#A89C98] hover:text-white" onClick={onClose}>
          <X size={20} />
        </button>

        {destination && !plan && !loading && (
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#C8A96E]/50 bg-[#C8A96E]/10 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#C8A96E]" data-testid="wellness-destination-chip">
            <Sparkles size={11} /> Planning your trip to {destination}
          </div>
        )}

        {!plan && !loading && <StepPicker step={step} onPick={pick} onBack={() => setStep(step - 1)} />}

        {loading && (
          <div className="py-16 text-center" data-testid="wellness-loading">
            <Sparkles size={32} className="text-[#C8A96E] mx-auto animate-pulse" />
            <p className="mt-4 text-[#A89C98]">Consulting your chart · aligning with your goals…</p>
          </div>
        )}

        {error && (
          <div className="py-12 text-center" data-testid="wellness-error">
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={reset} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none">Try again</Button>
          </div>
        )}

        {plan && <PlanResult plan={plan} onReset={reset} />}
      </div>
    </div>
  );
}
